import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Drama } from '@/mocks/dramas';

type Props = {
  dramas: Drama[];
};

export default function HeroCarousel({ dramas }: Props) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      setIndex(((next % dramas.length) + dramas.length) % dramas.length);
    },
    [dramas.length],
  );

  useEffect(() => {
    if (dramas.length <= 1) return;
    timer.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % dramas.length);
    }, 5500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [dramas.length]);

  const pause = () => {
    if (timer.current) window.clearInterval(timer.current);
  };
  const resume = () => {
    if (dramas.length <= 1) return;
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => setIndex((i) => (i + 1) % dramas.length), 5500);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) go(index + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  if (dramas.length === 0) return null;
  const active = dramas[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative h-[440px] w-full md:h-[540px] lg:h-[600px]">
        {dramas.map((d, i) => (
          <div
            key={d.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={d.backdrop}
              alt={`${d.title}主视觉剧照`}
              title={`${d.title} - 最新电视剧推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full max-w-content flex-col justify-center px-4 md:px-6">
          <div className="max-w-2xl animate-fade-up" key={active.id}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">热播推荐</span>
              <span className="rounded bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur">
                {active.categoryName}
              </span>
              {active.genres.map((g) => (
                <span key={g} className="rounded bg-background-50/10 px-2.5 py-1 text-xs text-background-100">
                  {g}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl lg:text-5xl">
              {active.title}
            </h1>
            <p className="mt-3 text-sm text-accent-300 md:text-base">{active.tagline}</p>
            <p className="clamp-3 mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {active.synopsis}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-400" />
                <strong className="font-heading text-background-50">{active.rating.toFixed(1)}</strong>
              </span>
              <span>主演：{active.starring.join(' / ')}</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {active.updateInfo}
              </span>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-circle-line text-lg" />
                查看剧情详情
              </Link>
              <Link
                to={`/category/${active.category}`}
                className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 bg-background-50/10 px-6 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
              >
                更多{active.categoryName}
              </Link>
            </div>
          </div>
        </div>

        {/* arrows */}
        <button
          type="button"
          aria-label="上一部"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:left-6 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:right-6 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        {/* dots */}
        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {dramas.map((d, i) => (
            <button
              key={d.id}
              type="button"
              aria-label={`切换到${d.title}`}
              onClick={() => go(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? 'w-6 bg-accent-500' : 'w-2 bg-background-50/40 hover:bg-background-50/70'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}